/**
 * screens/shell/overview.tsx
 * Authenticated landing — greeting, staff console card, trial banner,
 * role-filtered quick-link cards, and the onboarding checklist (P-005).
 * Faithful RN port of design/shell.jsx Overview + OnboardingChecklist.
 */

import React, { useState } from 'react';
import { View, Text, Pressable, ScrollView, StyleSheet } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { usePlatform } from '../../context/platformContext';
import { useShellNav } from '../../navigation/shellNav';
import { useResponsive } from '../../utils/responsive';
import { SettingsHeader, Tag } from '../../components/platform/primitives';
import Icon, { IconName } from '../../components/icon';
import Alert from '../../components/alert';
import { useToast } from '../../components/toast';
import { FS, FW, R } from '../../utils/platformTokens';
import type { Role } from '../../data/platformSeed';
import type { TenantScreen } from '../../navigation/types';
import { welcomeTitle } from '../../context/consoleIdentity';

const OnboardingChecklist: React.FC<{ onDismiss: () => void }> = ({ onDismiss }) => {
  const { theme } = useTheme();
  const c = theme.colors;
  const { navigate } = useShellNav();
  const toast = useToast();
  const [steps, setSteps] = useState([
    { id: 'verify', label: 'Email verified', done: true, optional: false, hint: 'anjali@acmeretail.in confirmed', link: false },
    { id: 'catalog', label: 'Create your catalog', done: false, optional: false, hint: 'Add your first product or service', link: true },
    { id: 'fields', label: 'Configure custom fields', done: false, optional: false, hint: 'Tailor records to your business', link: false },
    { id: 'voice', label: 'Connect Suki Voice', done: false, optional: false, hint: 'Route calls into the platform', link: false },
    { id: 'invite', label: 'Invite your team', done: false, optional: true, hint: 'Add teammates as ADMIN/EDITOR/VIEWER', link: false },
    { id: 'billing', label: 'Add a billing method', done: false, optional: false, hint: 'Required before trial ends', link: false },
  ]);
  const mandatory = steps.filter((s) => !s.optional);
  const doneCount = mandatory.filter((s) => s.done).length;
  const allDone = doneCount === mandatory.length;

  const complete = (id: string) => {
    setSteps((prev) => prev.map((s) => (s.id === id ? { ...s, done: true } : s)));
    toast.success('Step completed.');
  };
  const action = (s: typeof steps[number]) => {
    if (s.id === 'billing') navigate('billing');
    else if (s.id === 'invite') navigate('team');
    else complete(s.id);
  };

  return (
    <View style={[styles.card, { backgroundColor: c.card, borderColor: c.line }]}>
      <View style={[styles.cardHead, { borderColor: c.line }]}>
        <View>
          <Text style={{ fontSize: FS.md, fontWeight: FW.semibold, color: c.ink }}>Finish setting up</Text>
          <Text style={{ fontSize: FS.sm, color: c.ink3 }}>{doneCount} of {mandatory.length} required steps</Text>
        </View>
        <Pressable onPress={onDismiss} hitSlop={8}><Icon name="x" size={16} color={c.ink3} /></Pressable>
      </View>
      <View style={{ padding: 16, paddingTop: 12 }}>
        <View style={{ height: 6, backgroundColor: c.paper3, borderRadius: R.full, overflow: 'hidden', marginBottom: 14 }}>
          <View style={{ width: `${(doneCount / mandatory.length) * 100}%`, height: '100%', backgroundColor: c.accent }} />
        </View>
        {allDone && (
          <View style={{ marginBottom: 12 }}>
            <Alert tone="success" title="All set!">You can dismiss this checklist now.</Alert>
          </View>
        )}
        <View style={{ gap: 8 }}>
          {steps.map((s) => (
            <View
              key={s.id}
              style={{
                flexDirection: 'row', alignItems: 'center', gap: 10, padding: 10, borderRadius: R.r2,
                borderWidth: 1, borderColor: c.line, backgroundColor: s.done ? c.paper2 : c.card,
                opacity: s.done ? 0.85 : 1,
              }}
            >
              <View
                style={{
                  width: 20, height: 20, borderRadius: 10, alignItems: 'center', justifyContent: 'center',
                  backgroundColor: s.done ? c.success : c.paper3,
                  borderWidth: s.done ? 0 : 1, borderColor: c.lineStrong,
                }}
              >
                {s.done && <Icon name="check" size={12} color="#fff" />}
              </View>
              <View style={{ flex: 1, minWidth: 0 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                  <Text
                    style={{
                      fontSize: FS.md, fontWeight: FW.medium,
                      color: s.done ? c.ink3 : c.ink,
                      textDecorationLine: s.done ? 'line-through' : 'none',
                    }}
                  >
                    {s.label}
                  </Text>
                  {s.optional && <Tag tone="neutral">optional</Tag>}
                </View>
                {!s.done && <Text style={{ fontSize: FS.sm, color: c.ink3 }}>{s.hint}</Text>}
              </View>
              {!s.done && (
                <Pressable
                  onPress={() => action(s)}
                  style={{ height: 22, paddingHorizontal: 8, borderRadius: R.r2, backgroundColor: c.accent, justifyContent: 'center' }}
                >
                  <Text style={{ color: '#fff', fontSize: FS.xs, fontWeight: FW.semibold }}>{s.link ? 'Open' : 'Do it'}</Text>
                </Pressable>
              )}
            </View>
          ))}
        </View>
      </View>
    </View>
  );
};

const Overview: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const { state, role, isPlatformAdmin, onboardingDismissed, setOnboardingDismissed } = usePlatform();
  const { navigate, goApp } = useShellNav();
  const { width } = useResponsive();
  const toast = useToast();
  const showOnboarding = !onboardingDismissed;
  const twoCol = width > 960 && showOnboarding;

  const quick = (
    [
      // No member count: state.members is still the demo seed, not this org's team.
      { id: 'team', label: 'Team & Roles', desc: 'Teammates and what each can do', icon: 'users', roles: ['ADMIN'] },
      { id: 'plan', label: 'Plan & Usage', desc: 'Plans, usage and wallet per product', icon: 'star', roles: ['ADMIN', 'EDITOR'] },
      { id: 'billing', label: 'Billing', desc: 'Invoices & statements', icon: 'download', roles: ['ADMIN'] },
      { id: 'security', label: 'Security', desc: 'MFA, sessions & password', icon: 'lock', roles: ['ADMIN', 'EDITOR', 'VIEWER'] },
    ] as { id: TenantScreen; label: string; desc: string; icon: IconName; roles: Role[] }[]
  ).filter((q) => q.roles.includes(role));

  return (
    <ScrollView contentContainerStyle={{ padding: 28, maxWidth: 1200, width: '100%', alignSelf: 'center' }}>
      <SettingsHeader title={welcomeTitle(state.currentUser.name)} sub="Here's the state of your workspace." />

      {isPlatformAdmin && (
        <Pressable
          onPress={() => goApp('admin', 'admin-overview')}
          style={[styles.card, styles.staffCard, { backgroundColor: c.card, borderColor: c.line }]}
        >
          <View style={{ width: 38, height: 38, borderRadius: R.r2, backgroundColor: c.accent, alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="settings" size={19} color="#fff" />
          </View>
          <View style={{ flex: 1 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Text style={{ fontWeight: FW.semibold, fontSize: FS.md, color: c.ink }}>Platform Admin Console</Text>
              <Tag tone="info">Staff</Tag>
            </View>
            <Text style={{ fontSize: FS.sm, color: c.ink3 }}>Manage applications, plans and customer organizations.</Text>
          </View>
          <Icon name="chev-r" size={18} color={c.ink3} />
        </Pressable>
      )}

      <View style={{ flexDirection: twoCol ? 'row' : 'column', gap: 24, alignItems: 'flex-start' }}>
        <View style={{ flex: 1, alignSelf: 'stretch' }}>
          <View style={styles.quickGrid}>
            {quick.map((q) => (
              <Pressable
                key={q.id}
                onPress={() => navigate(q.id)}
                style={[styles.card, styles.quickCard, { backgroundColor: c.card, borderColor: c.line }]}
              >
                <View style={{ width: 36, height: 36, borderRadius: R.r2, backgroundColor: c.accentSoft, alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                  <Icon name={q.icon} size={18} color={c.accent} />
                </View>
                <Text style={{ fontWeight: FW.semibold, fontSize: FS.lg, color: c.ink }}>{q.label}</Text>
                <Text style={{ fontSize: FS.sm, color: c.ink3, marginTop: 2 }}>{q.desc}</Text>
              </Pressable>
            ))}
          </View>
        </View>
        {showOnboarding && (
          <View style={{ width: twoCol ? 360 : '100%' }}>
            <OnboardingChecklist onDismiss={() => setOnboardingDismissed(true)} />
          </View>
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  card: { borderWidth: 1, borderRadius: R.r3 },
  cardHead: {
    paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1,
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8,
  },
  staffCard: { flexDirection: 'row', alignItems: 'center', gap: 14, padding: 16, marginBottom: 20 },
  quickGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 14 },
  quickCard: { padding: 18, width: 220, flexGrow: 1 },
});

export default Overview;
